import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  CalendarIcon,
  BellIcon,
  BanknoteIcon,
  CheckIcon,
  MessageCircleIcon,
  ClockIcon,
  MoreVerticalIcon,
  Edit2Icon,
  Trash2Icon,
  SendIcon,
  PencilIcon,
  XIcon,
  CreditCard,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BottomNav } from '../components/BottomNav';
import { useAllAppointments, type AppointmentWithClient } from '../hooks/useAllAppointments';
import { useAllReminders } from '../hooks/useAllReminders';
import { useAllPayments, type Payment } from '../hooks/useAllPayments';
import { useUnifiedRemindersPayments, type UnifiedItem } from '../hooks/useUnifiedRemindersPayments';
import { useClients } from '../hooks/useClients';
import { useAuth } from '../contexts/AuthContext';
import { useWhatsAppStatus } from '../hooks/useWhatsAppStatus';
import { apiRequest } from '../lib/api';
import {
  useCreateAppointmentGlobal,
  useUpdateAppointmentGlobal,
  useDeleteAppointmentGlobal,
} from '../hooks/useAllAppointmentsMutations';
import { AppointmentListCard } from '../components/appointments/AppointmentListCard';
import {
  AppointmentFormModal,
  type AppointmentFormData,
} from '../components/appointments/AppointmentFormModal';
import { CancelAppointmentModal } from '../components/appointments/CancelAppointmentModal';
import { SearchableClientSelect } from '../components/common/SearchableClientSelect';
import ActionCard from '../components/common/ActionCard';
import EntityStatusBadge from '../components/common/EntityStatusBadge';
import { RemindersFilterChips } from '../components/schedule/RemindersFilterChips';
import { PaymentFormModal } from '../components/PaymentFormModal';
import { ReminderFormModal, CancelReminderModal } from '../components/reminders';
import { EditRecurringInstanceModal } from '../components/EditRecurringInstanceModal';
import { phoneNumberToWhatsAppJid } from '../../lib/whatsapp/jid';
import type { AppointmentTab } from '../types/appointment';
import type { Reminder } from '../types/reminders';
import type { ClientRecord } from '../hooks/useClients';

type MainTab = 'appointments' | 'reminders';
type ReminderFilter = 'all' | 'reminders' | 'payments';
type TimeTab = 'today' | 'upcoming' | 'overdue';

export function Schedule() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Initialize state from URL params
  const tabParam = searchParams.get('tab') as MainTab | null;
  const filterParam = searchParams.get('filter') as ReminderFilter | null;

  // Main tab state
  const [activeMainTab, setActiveMainTab] = useState<MainTab>(
    tabParam === 'reminders' || tabParam === 'appointments' ? tabParam : 'appointments'
  );

  // Reminder filter state (for when Reminders tab is active)
  const [reminderFilter, setReminderFilter] = useState<ReminderFilter>(
    filterParam === 'all' || filterParam === 'reminders' || filterParam === 'payments' ? filterParam : 'all'
  );

  // Time-based tab state
  const [appointmentTimeTab, setAppointmentTimeTab] = useState<AppointmentTab>('today');
  const [reminderTimeTab, setReminderTimeTab] = useState<TimeTab>('today');

  // Client filter state (from URL or 'all')
  const [selectedClientId, setSelectedClientId] = useState<string>(
    searchParams.get('client') || 'all'
  );

  // Form modal state for appointments
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<AppointmentWithClient | null>(null);
  const [cancellingAppointment, setCancellingAppointment] = useState<AppointmentWithClient | null>(null);

  // Form modal state for payments
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);

  // Fetch data
  const { appointments, isLoading: appointmentsLoading } = useAllAppointments(
    selectedClientId === 'all' ? undefined : selectedClientId
  );
  const { reminders, isLoading: remindersLoading } = useAllReminders();
  const { payments, isLoading: paymentsLoading } = useAllPayments();
  const { unified, isLoading: unifiedLoading } = useUnifiedRemindersPayments();
  const clientsQuery = useClients();
  const clients = clientsQuery.data || [];
  const whatsappStatus = useWhatsAppStatus();
  const whatsappConnected = Boolean(whatsappStatus.data?.connected);

  // Mutations for appointments
  const createAppointmentMutation = useCreateAppointmentGlobal();
  const updateAppointmentMutation = useUpdateAppointmentGlobal();
  const deleteAppointmentMutation = useDeleteAppointmentGlobal();

  // Pending state
  const [pendingReminderId, setPendingReminderId] = useState<string | null>(null);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Reminder edit/cancel state
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingReminder, setCancellingReminder] = useState<Reminder | null>(null);

  // Recurring reminder edit scope
  const [showEditScopeModal, setShowEditScopeModal] = useState(false);
  const [pendingEditReminder, setPendingEditReminder] = useState<Reminder | null>(null);
  const [editScope, setEditScope] = useState<'this_only' | 'all_future' | null>(null);

  // Floating menu state for Follow-Ups tab
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Create client map for WhatsApp navigation
  const clientsById = useMemo(() => {
    const map = new Map<string, ClientRecord>();
    (clientsQuery.data ?? []).forEach((client) => {
      map.set(client.id, client);
    });
    return map;
  }, [clientsQuery.data]);

  // Sync state with URL params
  useEffect(() => {
    if (tabParam && (tabParam === 'reminders' || tabParam === 'appointments')) {
      setActiveMainTab(tabParam);
    }
    if (filterParam && (filterParam === 'all' || filterParam === 'reminders' || filterParam === 'payments')) {
      setReminderFilter(filterParam);
    }
  }, [tabParam, filterParam]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Update URL when main tab changes
  const handleMainTabChange = (tab: MainTab) => {
    setActiveMainTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params);
  };

  // Update URL when client filter changes
  const handleClientFilterChange = (clientId: string) => {
    setSelectedClientId(clientId);
    const params = new URLSearchParams(searchParams);
    if (clientId === 'all') {
      params.delete('client');
    } else {
      params.set('client', clientId);
    }
    setSearchParams(params);
  };

  // Appointment handlers
  const handleOpenAppointmentForm = (appointment?: AppointmentWithClient) => {
    setEditingAppointment(appointment || null);
    setShowAppointmentForm(true);
  };

  const handleCloseAppointmentForm = () => {
    setShowAppointmentForm(false);
    setEditingAppointment(null);
  };

  const handleAppointmentFormSubmit = async (formData: AppointmentFormData & { clientId: string }) => {
    try {
      const payload = {
        title: formData.title,
        notes: formData.notes || undefined,
        starts_at: new Date(`${formData.date}T${formData.start_time}`).toISOString(),
        ends_at: new Date(`${formData.date}T${formData.end_time}`).toISOString(),
        meeting_type: formData.meeting_type,
        meeting_link: formData.meeting_link || undefined,
      };

      if (editingAppointment) {
        await updateAppointmentMutation.mutateAsync({
          clientId: formData.clientId,
          appointmentId: editingAppointment.id,
          payload,
        });
        setToast({ message: 'Appointment updated successfully', type: 'success' });
      } else {
        await createAppointmentMutation.mutateAsync({
          clientId: formData.clientId,
          payload,
        });
        setToast({ message: 'Appointment created successfully', type: 'success' });
      }

      handleCloseAppointmentForm();
    } catch (error) {
      console.error('Failed to save appointment:', error);
      setToast({ message: 'Failed to save appointment', type: 'error' });
    }
  };

  const handleAppointmentStatusChange = async (
    appointment: AppointmentWithClient,
    status: 'completed' | 'cancelled' | 'no_show'
  ) => {
    if (status === 'cancelled') {
      setCancellingAppointment(appointment);
      return;
    }

    try {
      await updateAppointmentMutation.mutateAsync({
        clientId: appointment.client_id,
        appointmentId: appointment.id,
        payload: { status },
      });
      const statusMessage =
        status === 'completed'
          ? 'Appointment marked as completed'
          : 'Appointment marked as no-show';
      setToast({ message: statusMessage, type: 'success' });
    } catch (error) {
      console.error('Failed to update status:', error);
      setToast({ message: 'Failed to update appointment', type: 'error' });
    }
  };

  const handleCancelConfirm = async (reason: string) => {
    if (!cancellingAppointment) return;

    try {
      await updateAppointmentMutation.mutateAsync({
        clientId: cancellingAppointment.client_id,
        appointmentId: cancellingAppointment.id,
        payload: {
          status: 'cancelled',
          cancellation_reason: reason,
        },
      });
      setToast({ message: 'Appointment cancelled', type: 'success' });
      setCancellingAppointment(null);
    } catch (error) {
      console.error('Failed to cancel:', error);
      setToast({ message: 'Failed to cancel appointment', type: 'error' });
    }
  };

  const handleDeleteAppointment = async (appointment: AppointmentWithClient) => {
    if (!confirm(`Delete "${appointment.title}"?`)) return;

    try {
      await deleteAppointmentMutation.mutateAsync({
        clientId: appointment.client_id,
        appointmentId: appointment.id,
      });
      setToast({ message: 'Appointment deleted', type: 'success' });
    } catch (error) {
      console.error('Failed to delete:', error);
      setToast({ message: 'Failed to delete appointment', type: 'error' });
    }
  };

  // Reminder handlers
  const markDoneMutation = useMutation({
    mutationFn: async (reminder: Reminder) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      await apiRequest(
        `/api/clients/${reminder.clientId}/reminders/${reminder.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: 'done' }),
        },
        session.access_token
      );
    },
    onMutate: (reminder) => {
      setPendingReminderId(reminder.id);
    },
    onSuccess: () => {
      setToast({ message: 'Reminder marked as done', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update reminder.';
      setToast({ message, type: 'error' });
    },
    onSettled: () => {
      setPendingReminderId(null);
    },
  });

  // Create reminder mutation
  const createReminderMutation = useMutation({
    mutationFn: async ({
      clientId,
      payload
    }: {
      clientId: string;
      payload: { title: string; description: string; due_at: string }
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      const response = await apiRequest(
        `/api/clients/${clientId}/reminders`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session.access_token
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      setToast({ message: 'Reminder created successfully', type: 'success' });
      setShowReminderForm(false);
      setEditingReminder(null);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to create reminder';
      setToast({ message, type: 'error' });
    },
  });

  // Update reminder mutation
  const updateReminderMutation = useMutation({
    mutationFn: async ({
      reminder,
      payload
    }: {
      reminder: Reminder;
      payload: { title: string; description: string; due_at: string; edit_scope?: string }
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      const response = await apiRequest(
        `/api/clients/${reminder.clientId}/reminders/${reminder.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        session.access_token
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      setToast({ message: 'Reminder updated successfully', type: 'success' });
      setShowReminderForm(false);
      setEditingReminder(null);
      setEditScope(null);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update reminder';
      setToast({ message, type: 'error' });
    },
  });

  // Cancel reminder mutation
  const cancelReminderMutation = useMutation({
    mutationFn: async ({
      reminder,
      reason
    }: {
      reminder: Reminder;
      reason: string
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      const response = await apiRequest(
        `/api/clients/${reminder.clientId}/reminders/${reminder.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status: 'cancelled',
            cancel_reason: reason
          }),
        },
        session.access_token
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      setToast({ message: 'Reminder cancelled', type: 'success' });
      setShowCancelModal(false);
      setCancellingReminder(null);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to cancel reminder';
      setToast({ message, type: 'error' });
    },
  });

  // Payment handlers
  const markPaidMutation = useMutation({
    mutationFn: async (payment: Payment) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      await apiRequest(
        `/api/clients/${payment.clientId}/payments/${payment.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: 'paid' }),
        },
        session.access_token
      );
    },
    onMutate: (payment) => {
      setPendingPaymentId(payment.id);
    },
    onSuccess: () => {
      setToast({ message: 'Payment marked as paid', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update payment.';
      setToast({ message, type: 'error' });
    },
    onSettled: () => {
      setPendingPaymentId(null);
    },
  });

  const deletePaymentMutation = useMutation({
    mutationFn: async (payment: Payment) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      await apiRequest(
        `/api/clients/${payment.clientId}/payments/${payment.id}`,
        {
          method: 'DELETE',
        },
        session.access_token
      );
    },
    onSuccess: () => {
      setToast({ message: 'Payment deleted', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setDeleteConfirmId(null);
      setMenuOpenId(null);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to delete payment.';
      setToast({ message, type: 'error' });
    },
  });

  const sendPaymentReminderMutation = useMutation({
    mutationFn: async (payment: Payment) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest(
        `/api/clients/${payment.clientId}/payments/${payment.id}/send-reminder`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        },
        session.access_token
      );
    },
    onSuccess: () => {
      setToast({ message: 'Payment reminder sent successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to send payment reminder.';
      setToast({ message, type: 'error' });
    },
  });

  // Reminder form handlers
  const handleEditReminder = (reminder: Reminder) => {
    // Check if this is a recurring instance
    if (reminder.is_recurring_instance) {
      setPendingEditReminder(reminder);
      setShowEditScopeModal(true);
    } else {
      // Direct edit for one-time reminders
      setEditingReminder(reminder);
      setShowReminderForm(true);
    }
  };

  const handleOpenNewReminderForm = () => {
    setShowFloatingMenu(false);
    setEditingReminder(null);
    setShowReminderForm(true);
  };

  const handleOpenNewPaymentForm = () => {
    setShowFloatingMenu(false);
    setEditingPayment(null);
    setShowPaymentForm(true);
  };

  const handleEditScopeChoice = (scope: 'this_only' | 'all_future') => {
    setEditScope(scope);
    setEditingReminder(pendingEditReminder);
    setShowReminderForm(true);
    setShowEditScopeModal(false);
  };

  const handleCancelReminder = (reminder: Reminder) => {
    setCancellingReminder(reminder);
    setShowCancelModal(true);
  };

  const handleReminderFormSubmit = (formData: {
    title: string;
    description: string;
    date: string;
    time: string;
    clientId?: string;
  }) => {
    // Combine date and time into ISO timestamp
    const dueAt = new Date(`${formData.date}T${formData.time}`).toISOString();

    const payload: any = {
      title: formData.title,
      description: formData.description,
      due_at: dueAt,
    };

    if (editingReminder) {
      // Update existing reminder
      // Include edit_scope for recurring instances
      if (editScope) {
        payload.edit_scope = editScope;
      }

      updateReminderMutation.mutate({
        reminder: editingReminder,
        payload
      });
    } else {
      // Create new reminder
      if (!formData.clientId) {
        setToast({
          message: 'Please select a client',
          type: 'error',
        });
        return;
      }

      createReminderMutation.mutate({
        clientId: formData.clientId,
        payload
      });
    }
  };

  const handleReminderCancelConfirm = (reason: string) => {
    if (!cancellingReminder) return;

    cancelReminderMutation.mutate({
      reminder: cancellingReminder,
      reason
    });
  };

  // Payment form handlers
  const handleOpenPaymentForm = (payment: Payment) => {
    setEditingPayment(payment);
    setShowPaymentForm(true);
    setMenuOpenId(null);
  };

  const handleClosePaymentForm = () => {
    setShowPaymentForm(false);
    setEditingPayment(null);
  };

  // WhatsApp navigation helpers
  const candidateJidsForClient = (client: ClientRecord) => {
    const digits = client.phone.replace(/\D/g, '');
    const candidates = [
      client.whatsapp_jid?.trim().toLowerCase() ?? null,
      phoneNumberToWhatsAppJid(client.phone),
      digits ? `${digits}@c.us` : null,
      digits ? `${digits}@l.whatsapp.net` : null,
    ];
    return candidates.reduce<string[]>((list, jid) => {
      if (jid && !list.includes(jid)) {
        list.push(jid);
      }
      return list;
    }, []);
  };

  const handleOpenChatFromItem = (item: UnifiedItem | Reminder | Payment) => {
    if (!whatsappConnected) {
      setToast({
        message: 'Connect WhatsApp to open a chat.',
        type: 'error',
      });
      return;
    }

    const client = clientsById.get(item.clientId);
    if (!client) {
      setToast({
        message: 'Unable to locate the client record.',
        type: 'error',
      });
      return;
    }

    const candidates = candidateJidsForClient(client);
    const targetJid = candidates[0] ?? null;

    if (!targetJid) {
      setToast({
        message: 'No WhatsApp contact linked to this client yet.',
        type: 'error',
      });
      return;
    }

    navigate(`/whatsapp/chat/${encodeURIComponent(targetJid)}`, {
      state: {
        clientId: client.id,
        clientName: client.name,
        clientPhone: client.phone,
        from: 'schedule',
        returnTo: {
          path: '/schedule',
          tab: activeMainTab,
          filter: reminderFilter,
          timeTab: activeMainTab === 'reminders' ? reminderTimeTab : appointmentTimeTab,
          clientFilter: selectedClientId,
        },
      },
    });
  };

  // Get filtered data based on active tabs and filters
  const getFilteredAppointments = () => {
    return appointments[appointmentTimeTab];
  };

  const getFilteredRemindersAndPayments = () => {
    const timeTabData = reminderFilter === 'all'
      ? unified[reminderTimeTab]
      : reminderFilter === 'reminders'
      ? reminders[reminderTimeTab]
      : payments[reminderTimeTab];

    // Apply client filter
    if (selectedClientId === 'all') {
      return timeTabData;
    }

    return timeTabData.filter((item: any) => item.clientId === selectedClientId);
  };

  const getTabCount = (tab: 'today' | 'upcoming' | 'overdue') => {
    // Get data for the specific tab based on current filter
    const timeTabData = reminderFilter === 'all'
      ? unified[tab]
      : reminderFilter === 'reminders'
      ? reminders[tab]
      : payments[tab];

    // Apply client filter if needed
    if (selectedClientId === 'all') {
      return timeTabData.length;
    }

    return timeTabData.filter((item: any) => item.clientId === selectedClientId).length;
  };

  // Helper functions for status colors and labels
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[#5B8E87] text-white';
      case 'lead':
        return 'bg-[#C9A870] text-white';
      case 'past':
        return 'bg-gray-400 text-white';
      default:
        return 'bg-gray-200 text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'lead':
        return 'Lead';
      case 'past':
        return 'Inactive';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Render functions
  const renderToast = () => {
    if (!toast) return null;
    const baseClass =
      toast.type === 'error'
        ? 'bg-red-50 text-red-700 border-red-200'
        : 'bg-green-50 text-green-700 border-green-200';
    return (
      <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${baseClass}`}>
        {toast.message}
      </div>
    );
  };

  const renderReminderCard = (reminder: Reminder) => {
    const isOverdue = reminderTimeTab === 'overdue';
    const isLoading = pendingReminderId === reminder.id && markDoneMutation.isPending;

    // Determine icon colors
    const iconBgColor = isOverdue ? 'bg-red-100' : 'bg-[#C9A870]/10';
    const iconColor = isOverdue ? 'text-red-600' : 'text-[#C9A870]';

    // Metadata
    const metadata = [
      { icon: <CalendarIcon className="w-3.5 h-3.5" />, text: reminder.dueDate },
      { icon: <ClockIcon className="w-3.5 h-3.5" />, text: reminder.dueTime },
    ];

    // Secondary actions
    const secondaryActions = [];
    if (reminder.clientWhatsappJid) {
      secondaryActions.push({
        icon: <MessageCircleIcon className="w-4 h-4" />,
        onClick: () => handleOpenChatFromItem(reminder),
        ariaLabel: 'Open Chat',
        variant: 'default' as const,
      });
    }

    // Menu items
    const menuItems = [
      {
        label: 'Edit',
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => handleEditReminder(reminder),
      },
      {
        label: 'Cancel',
        icon: <XIcon className="w-4 h-4" />,
        onClick: () => handleCancelReminder(reminder),
        variant: 'danger' as const,
      },
    ];

    // Map client status to ActionCard format
    const clientStatus = reminder.clientStatus === 'active'
      ? 'Active'
      : reminder.clientStatus === 'lead'
      ? 'Lead'
      : 'Past';

    // Handle plan navigation
    const handleNavigateToPlan = (planId: string) => {
      navigate(`/client/${reminder.clientId}?tab=plans&planId=${planId}`);
    };

    return (
      <ActionCard
        key={reminder.id}
        icon={<BellIcon className="w-5 h-5" />}
        iconBgColor={iconBgColor}
        iconColor={iconColor}
        clientName={reminder.clientName}
        clientStatus={clientStatus}
        clientId={reminder.clientId}
        title={reminder.message}
        metadata={metadata}
        planId={reminder.plan_id}
        planTitle={reminder.plan_title}
        onPlanClick={handleNavigateToPlan}
        primaryAction={{
          label: 'Mark Done',
          icon: <CheckIcon className="w-4 h-4" />,
          onClick: () => markDoneMutation.mutate(reminder),
          loading: isLoading,
          variant: 'success',
        }}
        secondaryActions={secondaryActions}
        menuItems={menuItems}
        actionsLayout="inline"
      />
    );
  };

  const renderPaymentCard = (payment: Payment) => {
    const isOverdue = reminderTimeTab === 'overdue';
    const isPaid = payment.status === 'paid';
    const isPending = payment.status === 'pending';
    const isLoading = pendingPaymentId === payment.id && markPaidMutation.isPending;

    // Determine icon colors
    const iconBgColor = isOverdue
      ? 'bg-red-100'
      : isPaid
      ? 'bg-[#5B8E87]/10'
      : 'bg-[#C9A870]/10';
    const iconColor = isOverdue
      ? 'text-red-600'
      : isPaid
      ? 'text-[#5B8E87]'
      : 'text-[#C9A870]';

    // Card state for border
    const cardState = isPaid ? 'completed' : 'default';

    // Title content (amount + notes)
    const titleContent = (
      <div>
        <div className="text-xl font-bold text-[#212121]">
          {formatCurrency(payment.amount, payment.currency)}
        </div>
        {payment.notes && (
          <div className="text-sm text-gray-500 italic mt-1">{payment.notes}</div>
        )}
      </div>
    );

    // Metadata
    const metadata = [
      { icon: <CalendarIcon className="w-3.5 h-3.5" />, text: payment.dueDate },
      { icon: <ClockIcon className="w-3.5 h-3.5" />, text: payment.dueTime },
      { icon: <CreditCard className="w-3.5 h-3.5" />, text: payment.paymentMethod || 'UPI' },
    ];

    // Status badge
    const statusBadge = (
      <EntityStatusBadge
        type="payment"
        status={isPaid ? 'Paid' : isOverdue ? 'Overdue' : 'Pending'}
      />
    );

    // Secondary actions
    const secondaryActions = [
      {
        icon: <MessageCircleIcon className="w-4 h-4" />,
        onClick: () => handleOpenChatFromItem(payment),
        ariaLabel: 'Open Chat',
        variant: 'default' as const,
      },
    ];

    // Menu items
    const menuItems = [];
    if (isPending) {
      menuItems.push({
        label: 'Send Reminder',
        icon: <SendIcon className="w-4 h-4" />,
        onClick: () => sendPaymentReminderMutation.mutate(payment),
      });
    }
    menuItems.push(
      {
        label: 'Edit',
        icon: <Edit2Icon className="w-4 h-4" />,
        onClick: () => handleOpenPaymentForm(payment),
      },
      {
        label: 'Delete',
        icon: <Trash2Icon className="w-4 h-4" />,
        onClick: () => {
          if (deleteConfirmId === payment.id) {
            deletePaymentMutation.mutate(payment);
            setDeleteConfirmId(null);
          } else {
            setDeleteConfirmId(payment.id);
          }
        },
        variant: 'danger' as const,
      }
    );

    // Map client status to ActionCard format
    const clientStatus = payment.clientStatus === 'active'
      ? 'Active'
      : payment.clientStatus === 'lead'
      ? 'Lead'
      : 'Past';

    return (
      <ActionCard
        key={payment.id}
        icon={<BanknoteIcon className="w-5 h-5" />}
        iconBgColor={iconBgColor}
        iconColor={iconColor}
        clientName={payment.clientName}
        clientStatus={clientStatus}
        clientId={payment.clientId}
        title={titleContent}
        metadata={metadata}
        statusBadge={statusBadge}
        cardState={cardState}
        primaryAction={
          isPending
            ? {
                label: 'Mark Paid',
                icon: <CheckIcon className="w-4 h-4" />,
                onClick: () => markPaidMutation.mutate(payment),
                loading: isLoading,
                variant: 'success',
              }
            : undefined
        }
        secondaryActions={secondaryActions}
        menuItems={menuItems}
        actionsLayout="inline"
      />
    );
  };

  const renderUnifiedCard = (item: UnifiedItem) => {
    if (item.type === 'reminder') {
      return renderReminderCard(item);
    } else {
      return renderPaymentCard(item as Payment);
    }
  };

  const renderContent = () => {
    if (activeMainTab === 'appointments') {
      const filteredAppointments = getFilteredAppointments();
      const isLoading = appointmentsLoading;

      return (
        <>
          {/* Time tabs for appointments */}
          <div className="flex border-b border-gray-200 overflow-x-auto bg-white">
            {(['today', 'upcoming', 'overdue', 'completed'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setAppointmentTimeTab(tab)}
                className={`flex-1 py-3 px-2 text-sm font-semibold whitespace-nowrap ${
                  appointmentTimeTab === tab
                    ? 'text-[#7B9EAE] border-b-2 border-[#7B9EAE]'
                    : 'text-gray-500'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {appointments[tab].length > 0 && (
                  <span className="ml-1">({appointments[tab].length})</span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4">
            {renderToast()}
            {isLoading ? (
              <div className="text-center p-8 text-gray-600">Loading appointments...</div>
            ) : filteredAppointments.length > 0 ? (
              <div className="space-y-3">
                {filteredAppointments.map((apt) => (
                  <AppointmentListCard
                    key={apt.id}
                    appointment={apt}
                    onEdit={handleOpenAppointmentForm}
                    onDelete={handleDeleteAppointment}
                    onStatusChange={handleAppointmentStatusChange}
                    onWhatsAppSuccess={(msg) => setToast({ message: msg, type: 'success' })}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#212121] mb-2">
                  No {appointmentTimeTab} appointments
                </h3>
                <p className="text-gray-600">
                  {appointmentTimeTab === 'today' && "You're all clear for today!"}
                  {appointmentTimeTab === 'upcoming' && 'No upcoming appointments scheduled.'}
                  {appointmentTimeTab === 'overdue' && 'Great job staying on track!'}
                  {appointmentTimeTab === 'completed' && 'No completed appointments yet.'}
                </p>
              </div>
            )}
          </div>
        </>
      );
    } else {
      // Reminders tab
      const filteredItems = getFilteredRemindersAndPayments();
      const isLoading = reminderFilter === 'all'
        ? unifiedLoading
        : reminderFilter === 'reminders'
        ? remindersLoading
        : paymentsLoading;

      return (
        <>
          {/* Filter chips */}
          <RemindersFilterChips
            activeFilter={reminderFilter}
            onChange={setReminderFilter}
          />

          {/* Time tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto bg-white">
            {(['today', 'upcoming', 'overdue'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setReminderTimeTab(tab)}
                className={`flex-1 py-3 px-2 text-sm font-semibold whitespace-nowrap ${
                  reminderTimeTab === tab
                    ? 'text-[#5B8E87] border-b-2 border-[#5B8E87]'
                    : 'text-gray-500'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {(() => {
                  const count = getTabCount(tab);
                  return count > 0 && <span className="ml-1">({count})</span>;
                })()}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4">
            {renderToast()}
            {isLoading ? (
              <div className="text-center p-8 text-gray-600">Loading...</div>
            ) : filteredItems.length > 0 ? (
              <div className="space-y-3">
                {filteredItems.map((item: any) =>
                  reminderFilter === 'all'
                    ? renderUnifiedCard(item)
                    : reminderFilter === 'reminders'
                    ? renderReminderCard(item)
                    : renderPaymentCard(item)
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <div className="w-20 h-20 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">
                    {reminderFilter === 'payments' ? '💰' : '🎉'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-[#212121] mb-2">
                  No {reminderTimeTab} {reminderFilter === 'all' ? 'items' : reminderFilter}
                </h3>
                <p className="text-gray-600">
                  {reminderTimeTab === 'today' && "You're all caught up for today!"}
                  {reminderTimeTab === 'upcoming' && 'You have nothing scheduled coming up.'}
                  {reminderTimeTab === 'overdue' && 'Great job staying on top of things!'}
                </p>
              </div>
            )}
          </div>
        </>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-[#212121]">Schedule</h1>
        </div>

        {/* Main tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => handleMainTabChange('appointments')}
            className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 ${
              activeMainTab === 'appointments'
                ? 'text-[#5B8E87] border-b-2 border-[#5B8E87]'
                : 'text-gray-500'
            }`}
          >
            <CalendarIcon className="w-5 h-5" />
            <span>Appointments</span>
          </button>
          <button
            onClick={() => handleMainTabChange('reminders')}
            className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 ${
              activeMainTab === 'reminders'
                ? 'text-[#5B8E87] border-b-2 border-[#5B8E87]'
                : 'text-gray-500'
            }`}
          >
            <BellIcon className="w-5 h-5" />
            <span>Follow-Ups</span>
          </button>
        </div>
      </div>

      {/* Client Filter */}
      <div className="p-4 bg-white border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Client</label>
        <SearchableClientSelect
          clients={clients}
          value={selectedClientId}
          onChange={handleClientFilterChange}
        />
      </div>

      {/* Dynamic content based on active main tab */}
      {renderContent()}

      {/* Floating Action Button */}
      <button
        onClick={() => {
          if (activeMainTab === 'appointments') {
            handleOpenAppointmentForm();
          } else {
            // For reminders tab, toggle floating menu
            setShowFloatingMenu(!showFloatingMenu);
          }
        }}
        className={`fixed bottom-24 right-6 text-white w-14 h-14 rounded-full shadow-lg transition-colors z-30 flex items-center justify-center ${
          activeMainTab === 'appointments'
            ? 'bg-[#7B9EAE] hover:bg-[#6A8A9A]'
            : 'bg-[#5B8E87] hover:bg-[#4F7A74]'
        }`}
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {/* Floating Menu for Follow-Ups */}
      {activeMainTab === 'reminders' && showFloatingMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-20"
            onClick={() => setShowFloatingMenu(false)}
          />
          {/* Menu */}
          <div className="fixed bottom-40 right-6 z-40 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden min-w-[200px]">
            <button
              onClick={handleOpenNewReminderForm}
              className="w-full px-5 py-4 text-left hover:bg-gray-50 flex items-center gap-3 text-sm font-medium text-gray-700 transition-colors"
            >
              <BellIcon className="w-5 h-5 text-[#C9A870]" />
              Add Reminder
            </button>
            <div className="border-t border-gray-100" />
            <button
              onClick={handleOpenNewPaymentForm}
              className="w-full px-5 py-4 text-left hover:bg-gray-50 flex items-center gap-3 text-sm font-medium text-gray-700 transition-colors"
            >
              <BanknoteIcon className="w-5 h-5 text-[#5B8E87]" />
              Add Payment
            </button>
          </div>
        </>
      )}

      {/* Modals */}
      {activeMainTab === 'appointments' && (
        <>
          <AppointmentFormModal
            isOpen={showAppointmentForm}
            onClose={handleCloseAppointmentForm}
            onSubmit={handleAppointmentFormSubmit}
            isSubmitting={createAppointmentMutation.isPending || updateAppointmentMutation.isPending}
            editingAppointment={editingAppointment}
            clientId={editingAppointment?.client_id}
            clients={clients}
          />

          {cancellingAppointment && (
            <CancelAppointmentModal
              isOpen={Boolean(cancellingAppointment)}
              onClose={() => setCancellingAppointment(null)}
              onConfirm={handleCancelConfirm}
              appointmentTitle={cancellingAppointment.title}
              isSubmitting={updateAppointmentMutation.isPending}
            />
          )}
        </>
      )}

      {activeMainTab === 'reminders' && (
        <PaymentFormModal
          isOpen={showPaymentForm}
          onClose={handleClosePaymentForm}
          editingPayment={editingPayment}
          onSuccess={(message) => setToast({ message, type: 'success' })}
          onError={(message) => setToast({ message, type: 'error' })}
        />
      )}

      {/* Reminder Form Modal */}
      {showReminderForm && (
        <ReminderFormModal
          isOpen={showReminderForm}
          onClose={() => {
            setShowReminderForm(false);
            setEditingReminder(null);
            setEditScope(null);
          }}
          onSubmit={handleReminderFormSubmit}
          editingReminder={editingReminder ? {
            id: editingReminder.id,
            title: editingReminder.message,
            description: '',
            due_at: editingReminder.dueAt,
          } : null}
          clientId={editingReminder?.clientId || selectedClientId}
          isLoading={createReminderMutation.isPending || updateReminderMutation.isPending}
          error={null}
        />
      )}

      {/* Cancel Reminder Modal */}
      {showCancelModal && cancellingReminder && (
        <CancelReminderModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setCancellingReminder(null);
          }}
          onConfirm={handleReminderCancelConfirm}
          reminderTitle={cancellingReminder.message}
          isLoading={cancelReminderMutation.isPending}
          error={null}
        />
      )}

      {/* Edit Recurring Instance Modal */}
      {showEditScopeModal && pendingEditReminder && (
        <EditRecurringInstanceModal
          onClose={() => {
            setShowEditScopeModal(false);
            setPendingEditReminder(null);
          }}
          onEditThisOnly={() => handleEditScopeChoice('this_only')}
          onEditAllFuture={() => handleEditScopeChoice('all_future')}
          reminderTitle={pendingEditReminder.message}
        />
      )}

      <BottomNav />
    </div>
  );
}
